import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, Spinner, WarningCircle, ClockCounterClockwise, Path, FolderOpen } from "@phosphor-icons/react";

const STATUS_META = {
  completed: { label: "Completed", icon: CheckCircle, dot: "bg-emerald-500", text: "text-emerald-600" },
  running: { label: "Running", icon: Spinner, dot: "bg-sky-500 animate-pulse", text: "text-sky-600" },
  queued: { label: "Queued", icon: ClockCounterClockwise, dot: "bg-amber-500", text: "text-amber-600" },
  failed: { label: "Failed", icon: WarningCircle, dot: "bg-rose-500", text: "text-rose-600" },
};

export default function Workflows() {
  const [runs, setRuns] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all|running|completed|failed

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: wfData }, { data: wsData }] = await Promise.all([
          api.get("/workflows"),
          api.get("/workspaces"),
        ]);
        if (cancelled) return;
        setRuns(Array.isArray(wfData) ? wfData : []);
        setWorkspaces(Array.isArray(wsData) ? wsData : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const wsById = useMemo(() => Object.fromEntries(workspaces.map((w) => [w.id, w])), [workspaces]);

  const counts = useMemo(() => {
    const c = { all: runs.length, running: 0, completed: 0, failed: 0, queued: 0 };
    for (const r of runs) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [runs]);

  const filtered = useMemo(() => {
    if (filter === "all") return runs;
    return runs.filter((r) => r.status === filter);
  }, [runs, filter]);

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-screen-2xl" data-testid="workflows-index-page">
      <div className="text-overline text-primary">Operations</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">All workflow runs</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        Every workflow ever launched in your organisation — across all workspaces. Filter by status to triage execution health.
      </p>

      {/* Filter tabs */}
      <div className="mt-8 flex flex-wrap items-center gap-2" role="tablist" data-testid="workflows-filter-tabs">
        {[
          ["all", "All"],
          ["running", "Running"],
          ["completed", "Completed"],
          ["failed", "Failed"],
          ["queued", "Queued"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`workflows-filter-${key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === key
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {label} <span className="ml-1 opacity-70">{counts[key] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="mt-10 text-overline text-muted-foreground" data-testid="workflows-loading">Loading runs…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 border border-dashed border-border rounded-2xl p-12 text-center" data-testid="workflows-empty">
          <Path size={28} weight="duotone" className="mx-auto text-muted-foreground/60" />
          <div className="mt-3 font-display text-xl tracking-tight">No runs in this view</div>
          <div className="text-sm text-muted-foreground mt-1">
            {filter === "all"
              ? "Launch your first workflow from a workspace."
              : `No workflows currently in ${filter} status.`}
          </div>
          <Link to="/workspaces" data-testid="workflows-empty-cta" className="inline-flex items-center gap-2 mt-5 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors">
            <FolderOpen size={14} weight="bold" /> Go to workspaces
          </Link>
        </div>
      ) : (
        <div className="mt-8 divide-y divide-border border border-border rounded-2xl overflow-hidden bg-card" data-testid="workflows-list">
          {filtered.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.queued;
            const StatusIcon = meta.icon;
            const ws = wsById[r.workspace_id];
            const started = (r.created_at || "").slice(0, 16).replace("T", " ");
            return (
              <Link
                key={r.id}
                to={`/workflows/run/${r.id}`}
                data-testid={`workflows-row-${r.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-secondary/40 transition-colors"
              >
                <span className={`h-2.5 w-2.5 rounded-full ${meta.dot} shrink-0`} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-base tracking-tight truncate">
                      {r.workflow_label || r.workflow_id?.replace(/_/g, " ")}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${meta.text}`}>
                      <StatusIcon size={11} weight="bold" className="inline mr-0.5" /> {meta.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono mt-1 truncate">
                    {ws && (
                      <span className="inline-flex items-center gap-1 min-w-0 truncate">
                        <FolderOpen size={11} weight="bold" /> <span className="truncate">{ws.name}</span>
                      </span>
                    )}
                    {started && <span>· {started}</span>}
                    {r.current_step && <span className="truncate">· {r.current_step}</span>}
                  </div>
                </div>
                <div className="hidden sm:flex flex-col items-end shrink-0 w-32">
                  <div className="text-overline text-muted-foreground">Progress</div>
                  <div className="text-sm font-mono mt-0.5">{r.progress ?? 0}%</div>
                  <div className="mt-1 w-full h-1 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${r.progress ?? 0}%` }} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
